import { Checkbox, styled, Typography } from '@mui/material';
import SearchBar from 'components/SharedComponents/SearchBar/SearchBar';
import React, { memo, useCallback, useMemo, useState } from 'react';

import {
    getDisplayableTreeAndSelectableValuesAndExpandedValues,
    getIsNodeMatchingSearch,
} from './helpers';
import { SelectableList } from './SelectableList';
import SelectAllCheckbox from './SelectAllCheckbox';
import { Tree } from './Tree';
import { TreeWithDisplayOptionsT, TreeWithOptionalIsDisabled } from './types';

const StyContainer = styled('div', {
    name: 'StyContainer',
})(() => ({
    display: 'flex',
    flexDirection: 'column',
    minHeight: 400,
    flexGrow: 1,
}));

const StyHeader = styled('div', {
    name: 'StyHeader',
})(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    '& > *': {
        marginBottom: theme.spacing(2),
    },
}));

const StyTreeContainer = styled('div', {
    name: 'StyTreeContainer',
})(() => ({
    flex: 1,
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'column',
    overflow: 'auto',
}));

const Node = memo(
    ({
        isChecked,
        node,
        handleNodeSelect,
    }: {
        isChecked: boolean;
        node: TreeWithDisplayOptionsT;
        handleNodeSelect: (event: React.MouseEvent, node: TreeT) => void;
    }) => (
        <>
            <Checkbox
                checked={isChecked}
                onClick={(event) => handleNodeSelect(event, node)}
                disabled={node.isDisabled}
            />
            <Typography
                variant={node.isBold ? 'body1Bold' : 'body1'}
                component="span"
            >
                {node.label}
            </Typography>
        </>
    ),
);

const getWrappedHandleNodeSelect =
    (handleNodeSelect: (node: TreeT) => void) =>
    (event: React.MouseEvent, node: TreeT) => {
        event.stopPropagation();
        handleNodeSelect(node);
    };

type Props = {
    tree: TreeWithOptionalIsDisabled;
    selectedNodeValues: string[];
    setSelectedNodeValues: React.Dispatch<React.SetStateAction<string[]>>;
    defaultExpandedValues?: string[];
    handleNodeSelect?: (node: TreeT) => void;
    withSearch?: boolean;
    withSelectAll?: boolean;
    isFlatTree?: boolean;
};

const SelectableTree = ({
    tree,
    selectedNodeValues,
    setSelectedNodeValues,
    defaultExpandedValues,
    handleNodeSelect: handleNodeSelectProps,
    withSearch = false,
    withSelectAll = false,
    isFlatTree = false,
}: Props) => {
    const hasHeader = withSearch || withSearch;
    const [searchedText, setSearchedText] = useState<string>('');

    const {
        processedTree: initialTree,
        selectableValues: initialSelectableValues,
    } = useMemo(() => {
        if (isFlatTree) {
            return {
                processedTree: null,
                selectableValues: tree.children.map((c) => c.value),
            };
        }
        return getDisplayableTreeAndSelectableValuesAndExpandedValues(
            tree,
            getIsNodeMatchingSearch(''),
        );
    }, [tree, isFlatTree]);

    const [displayedTree, setDisplayedTree] =
        useState<TreeWithDisplayOptionsT | null>(initialTree);
    const [expandedValues, setExpandedValues] = useState<string[]>(
        defaultExpandedValues || [],
    );
    const [selectableValues, setSelectableValues] = useState<string[]>(
        initialSelectableValues,
    );

    const handleInputChange = useCallback(
        (newValue: string) => {
            setSearchedText(newValue);
            const {
                processedTree: newProcessedTree,
                selectableValues: newSelectableValues,
                expandedValues: newExpandedValues,
            } = getDisplayableTreeAndSelectableValuesAndExpandedValues(
                tree,
                getIsNodeMatchingSearch(newValue),
            );
            setDisplayedTree(newProcessedTree);
            setSelectableValues(newSelectableValues);
            setExpandedValues(newExpandedValues);
        },
        [tree, getIsNodeMatchingSearch],
    );

    const defaultHandleNodeSelect = useCallback(
        (node: TreeT) => {
            setSelectedNodeValues((prevSelectedNodeValues) => {
                if (prevSelectedNodeValues.includes(node.value)) {
                    return prevSelectedNodeValues.filter(
                        (value) => value !== node.value,
                    );
                }
                return [...prevSelectedNodeValues, node.value];
            });
        },
        [setSelectedNodeValues],
    );

    const handleNodeSelect = useMemo(
        () =>
            getWrappedHandleNodeSelect(
                handleNodeSelectProps || defaultHandleNodeSelect,
            ),
        [defaultHandleNodeSelect, handleNodeSelectProps],
    );

    const rowRenderer = useCallback(
        (node: TreeWithOptionalIsDisabled) => {
            const isChecked = selectedNodeValues.includes(node.value);
            return (
                <Node
                    isChecked={isChecked}
                    node={node}
                    handleNodeSelect={handleNodeSelect}
                />
            );
        },
        [handleNodeSelect, selectedNodeValues],
    );

    const flatTreeValues = useMemo(
        () =>
            isFlatTree
                ? tree.children.filter((node) =>
                      getIsNodeMatchingSearch(searchedText)(node.label),
                  )
                : [],
        [isFlatTree, getIsNodeMatchingSearch, searchedText, tree.children],
    );

    const selector = isFlatTree ? (
        <SelectableList values={flatTreeValues} rowRenderer={rowRenderer} />
    ) : (
        <Tree
            tree={displayedTree}
            rowRenderer={rowRenderer}
            expandedValues={expandedValues}
            setExpanded={setExpandedValues}
        />
    );

    return (
        <StyContainer>
            {hasHeader && (
                <StyHeader>
                    {withSearch && (
                        // TODO: Use design-system SearchBar instead
                        <SearchBar onChange={handleInputChange} width="lg" />
                    )}
                    {withSelectAll && (
                        <SelectAllCheckbox
                            selectedNodeValues={selectedNodeValues}
                            setSelectedNodeValues={setSelectedNodeValues}
                            allSelectableNodeValues={selectableValues}
                        />
                    )}
                </StyHeader>
            )}
            <StyTreeContainer>{selector}</StyTreeContainer>
        </StyContainer>
    );
};

export default SelectableTree;
